import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock3, Loader2, Check, X, Trash2, Phone, MessageCircle } from "lucide-react";

const dstr = (d) => d.toISOString().slice(0, 10);

export const WaitlistManager = ({ services = [], team = [], onApproved }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [form, setForm] = useState({ date: "", time: "", operator_id: "", service_ids: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/waitlist?status=waiting");
      setItems(data);
    } catch (e) { setItems([]); toast.error("Errore caricamento lista d'attesa"); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const startApprove = (w) => {
    setForm({
      date: (w.days && w.days[0]) || dstr(new Date()),
      time: w.preferred_time || "10:00",
      operator_id: w.operator_id || (team[0]?.id || ""),
      service_ids: w.service_ids || [],
    });
    setApproveTarget(w);
  };

  const confirmApprove = async () => {
    if (!form.date || !form.time || !form.operator_id || !(form.service_ids || []).length) {
      toast.error("Conferma giorno, ora, professionista e servizi"); return;
    }
    setBusy(true);
    try {
      await api.post(`/waitlist/${approveTarget.id}/approve`, form);
      toast.success("Richiesta approvata: appuntamento creato e cliente avvisata");
      setApproveTarget(null);
      await load();
      onApproved && onApproved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore approvazione"); }
    finally { setBusy(false); }
  };

  const reject = async (w) => {
    try { await api.post(`/waitlist/${w.id}/reject`, {}); toast.success("Richiesta rifiutata"); load(); }
    catch (e) { toast.error("Errore"); }
  };
  const remove = async (w) => {
    try { await api.delete(`/waitlist/${w.id}`); toast.success("Richiesta archiviata"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  const svcName = (id) => services.find((s) => s.id === id)?.name || id;
  const count = items?.length || 0;

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="agenda-waitlist-button">
        <Clock3 className="h-4 w-4 mr-1" /> Lista d'attesa{count > 0 ? ` (${count})` : ""}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto hi-scroll" data-testid="waitlist-manager-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Clock3 className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Lista d'attesa</DialogTitle></DialogHeader>

          {items === null && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--hi-gold))]" /></div>}
          {items !== null && items.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center" data-testid="waitlist-empty">Nessuna richiesta in attesa.</p>}

          <div className="space-y-2">
            {(items || []).map((w) => (
              <div key={w.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`waitlist-item-${w.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{w.client_name}</div>
                    <div className="text-sm text-muted-foreground">{(w.service_names || w.service_ids?.map(svcName) || []).join(", ")}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Giorno: {(w.days || []).join(", ") || "—"}
                      {" · "}Orario: {w.any_time ? "qualsiasi" : (w.preferred_time || "—")}
                      {w.operator_name ? ` · Prof.: ${w.operator_name}` : ""}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Richiesta: {new Date(w.created_at).toLocaleString("it-IT")}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] shrink-0">In attesa</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button size="sm" className="h-7 px-2 text-xs" onClick={() => startApprove(w)} data-testid={`waitlist-approve-${w.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approva</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => reject(w)} data-testid={`waitlist-reject-${w.id}`}><X className="h-3.5 w-3.5 mr-1" /> Rifiuta</Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[hsl(var(--hi-status-danger))]" onClick={() => remove(w)} data-testid={`waitlist-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina</Button>
                  {w.contact_phone && (
                    <>
                      <a href={`tel:${w.contact_phone}`} data-testid={`waitlist-call-${w.id}`}><Button size="sm" variant="ghost" className="h-7 px-2 text-xs"><Phone className="h-3.5 w-3.5 mr-1" /> Chiama</Button></a>
                      <a href={`https://wa.me/${(w.contact_phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" data-testid={`waitlist-wa-${w.id}`}><Button size="sm" variant="ghost" className="h-7 px-2 text-xs"><MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp</Button></a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Conferma approvazione: giorno, ora, professionista, servizi */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => { if (!o) setApproveTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="waitlist-approve-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Conferma appuntamento</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Conferma i dettagli: verrà creato l'appuntamento e inviato il messaggio di conferma a {approveTarget?.client_name}.</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Giorno</Label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="waitlist-approve-date" /></div>
              <div className="space-y-1.5"><Label>Ora</Label><Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} data-testid="waitlist-approve-time" /></div>
            </div>
            <div className="space-y-1.5"><Label>Professionista</Label>
              <Select value={form.operator_id} onValueChange={(v) => setForm((f) => ({ ...f, operator_id: v }))}>
                <SelectTrigger data-testid="waitlist-approve-operator"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Servizi richiesti</Label>
              <div className="rounded-md border border-border p-2 max-h-40 overflow-y-auto hi-scroll space-y-1">
                {services.map((s) => {
                  const on = (form.service_ids || []).includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => setForm((f) => ({ ...f, service_ids: on ? f.service_ids.filter((x) => x !== s.id) : [...f.service_ids, s.id] }))}
                      className={`w-full flex justify-between items-center text-left text-sm rounded px-2 py-1.5 ${on ? "bg-[hsl(var(--hi-gold-soft))]" : "hover:bg-accent"}`} data-testid={`waitlist-approve-svc-${s.id}`}>
                      <span>{s.name}</span>{on && <Check className="h-3.5 w-3.5 text-[hsl(var(--hi-gold))]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)} data-testid="waitlist-approve-cancel">Annulla</Button>
            <Button onClick={confirmApprove} disabled={busy} data-testid="waitlist-approve-confirm">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma e crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
